import Vue from 'vue'
import Vuex from 'vuex'
import Meta from 'vue-meta'
import ClientOnly from 'vue-client-only'
import NoSsr from 'vue-no-ssr'
import { createRouter } from './router.js'
import NuxtChild from './components/nuxt-child.js'
import NuxtError from '../src/layouts/error.vue'
import Nuxt from './components/nuxt.js'
import App from './App.js'
import { setContext, getLocation, getRouteData, normalizeError } from './utils'
import { createStore } from './store.js'

/* Plugins */

import nuxt_plugin_sentryserver_8fb869ac from 'nuxt_plugin_sentryserver_8fb869ac' // Source: ./sentry.server.js (mode: 'server')
import nuxt_plugin_sentryclient_526a59a2 from 'nuxt_plugin_sentryclient_526a59a2' // Source: ./sentry.client.js (mode: 'client')
import nuxt_plugin_axios_fc625d34 from 'nuxt_plugin_axios_fc625d34' // Source: ./axios.js (mode: 'all')
import nuxt_plugin_axios_3566aa80 from 'nuxt_plugin_axios_3566aa80' // Source: ../src/plugins/axios (mode: 'all')
import nuxt_plugin_seo_5e466527 from 'nuxt_plugin_seo_5e466527' // Source: ../src/plugins/seo (mode: 'all')
import nuxt_plugin_filters_98405076 from 'nuxt_plugin_filters_98405076' // Source: ../src/plugins/filters (mode: 'all')
import nuxt_plugin_tracking_3ed1642d from 'nuxt_plugin_tracking_3ed1642d' // Source: ../src/plugins/tracking (mode: 'all')
import nuxt_plugin_modals_4f7cf348 from 'nuxt_plugin_modals_4f7cf348' // Source: ../src/plugins/modals (mode: 'client')
import nuxt_plugin_gtag_6a810249 from 'nuxt_plugin_gtag_6a810249' // Source: ../src/plugins/gtag (mode: 'client')
import nuxt_plugin_global_6441a2ce from 'nuxt_plugin_global_6441a2ce' // Source: ../src/plugins/global (mode: 'all')
import nuxt_plugin_directives_d0867c0c from 'nuxt_plugin_directives_d0867c0c' // Source: ../src/plugins/directives (mode: 'client')
import nuxt_plugin_copy_6a7f1feb from 'nuxt_plugin_copy_6a7f1feb' // Source: ../src/plugins/copy (mode: 'all')
import nuxt_plugin_jsonld_cea01772 from 'nuxt_plugin_jsonld_cea01772' // Source: ../src/plugins/json-ld (mode: 'all')
import nuxt_plugin_campaign_2a7f72f4 from 'nuxt_plugin_campaign_2a7f72f4' // Source: ../src/plugins/campaign (mode: 'server')

// Component: <ClientOnly>
Vue.component(ClientOnly.name, ClientOnly)

// TODO: Remove in Nuxt 3: <NoSsr>
Vue.component(NoSsr.name, {
  ...NoSsr,
  render (h, ctx) {
    if (process.client && !NoSsr._warned) {
      NoSsr._warned = true

      console.warn('<no-ssr> has been deprecated and will be removed in Nuxt 3, please use <client-only> instead')
    }
    return NoSsr.render(h, ctx)
  }
})

// Component: <NuxtChild>
Vue.component(NuxtChild.name, NuxtChild)
Vue.component('NChild', NuxtChild)

// Component NuxtLink is imported in server.js or client.js

// Component: <Nuxt>
Vue.component(Nuxt.name, Nuxt)

Object.defineProperty(Vue.prototype, '$app', {
  get() {
    const globalNuxt = this.$root ? this.$root.$options.$app : null
    if (process.client && !globalNuxt && typeof window !== 'undefined') {
      return window.$app
    }
    return globalNuxt
  },
  configurable: true
})

Vue.use(Meta, {"keyName":"head","attribute":"data-n-head","ssrAttribute":"data-n-head-ssr","tagIDKeyName":"hid"})

const defaultTransition = {"name":"page","mode":"out-in","appear":false,"appearClass":"appear","appearActiveClass":"appear-active","appearToClass":"appear-to"}

const originalRegisterModule = Vuex.Store.prototype.registerModule

function registerModule (path, rawModule, options = {}) {
  const preserveState = process.client && (
    Array.isArray(path)
      ? !!path.reduce((namespacedState, path) => namespacedState && namespacedState[path], this.state)
      : path in this.state
  )
  return originalRegisterModule.call(this, path, rawModule, { preserveState, ...options })
}

async function createApp(ssrContext, config = {}) {
  const store = createStore(ssrContext)
  const router = await createRouter(ssrContext, config, { store })

  // Add this.$router into store actions/mutations
  store.$router = router

  // Fix SSR caveat https://github.com/nuxt/nuxt.js/issues/3757#issuecomment-414689141
  store.registerModule = registerModule

  // Create Root instance

  // here we inject the router and store to all child components,
  // making them available everywhere as `this.$router` and `this.$store`.
  const app = {
    head: {"title":" | 365 Financial Analyst","htmlAttrs":{"lang":"en"},"meta":[{"charset":"utf-8"},{"name":"viewport","content":"width=device-width, initial-scale=1"},{"name":"msapplication-TileColor","content":"#011633"},{"name":"msapplication-config","content":"https:\u002F\u002F365financialanalyst.com\u002Fbrowserconfig.xml"},{"name":"theme-color","content":"#011633"}],"link":[{"rel":"manifest","href":"https:\u002F\u002F365financialanalyst.com\u002Fsite.webmanifest"},{"rel":"apple-touch-icon","sizes":"180x180","href":"https:\u002F\u002F365financialanalyst.com\u002Fresources\u002Ffavicons\u002Fapple-touch-icon.png"},{"rel":"apple-touch-icon","type":"image\u002Fpng","sizes":"32x32","href":"https:\u002F\u002F365financialanalyst.com\u002Fresources\u002Ffavicons\u002Ffavicon-32x32.png"},{"rel":"apple-touch-icon","type":"image\u002Fpng","sizes":"16x16","href":"https:\u002F\u002F365financialanalyst.com\u002Fresources\u002Ffavicons\u002Ffavicon-16x16.png"},{"rel":"mask-icon","href":"https:\u002F\u002F365financialanalyst.com\u002Fresources\u002Ffavicons\u002Fsafari-pinned-tab.svg","color":"#5bbad5"},{"rel":"shortcut icon","href":"https:\u002F\u002F365financialanalyst.com\u002Fresources\u002Ffavicons\u002Ffavicon.ico"},{"rel":"icon","type":"image\u002Fx-icon","href":"https:\u002F\u002F365financialanalyst.com\u002Ffavicon.ico"}],"style":[],"script":[]},

    store,
    router,
    nuxt: {
      defaultTransition,
      transitions: [defaultTransition],
      setTransitions (transitions) {
        if (!Array.isArray(transitions)) {
          transitions = [transitions]
        }
        transitions = transitions.map((transition) => {
          if (!transition) {
            transition = defaultTransition
          } else if (typeof transition === 'string') {
            transition = Object.assign({}, defaultTransition, { name: transition })
          } else {
            transition = Object.assign({}, defaultTransition, transition)
          }
          return transition
        })
        this.$options.nuxt.transitions = transitions
        return transitions
      },

      err: null,
      dateErr: null,
      error (err) {
        err = err || null
        app.context._errored = Boolean(err)
        err = err ? normalizeError(err) : null
        let nuxt = app.nuxt // to work with @vue/composition-api, see https://github.com/nuxt/nuxt.js/issues/6517#issuecomment-573280207
        if (this) {
          nuxt = this.nuxt || this.$options.nuxt
        }
        nuxt.dateErr = Date.now()
        nuxt.err = err
        // Used in src/server.js
        if (ssrContext) {
          ssrContext.nuxt.error = err
        }
        return err
      }
    },
    ...App
  }

  // Make app available into store via this.app
  store.app = app

  const next = ssrContext ? ssrContext.next : location => app.router.push(location)
  // Resolve route
  let route
  if (ssrContext) {
    route = router.resolve(ssrContext.url).route
  } else {
    const path = getLocation(router.options.base, router.options.mode)
    route = router.resolve(path).route
  }

  // Set context to app.context
  await setContext(app, {
    store,
    route,
    next,
    error: app.nuxt.error.bind(app),
    payload: ssrContext ? ssrContext.payload : undefined,
    req: ssrContext ? ssrContext.req : undefined,
    res: ssrContext ? ssrContext.res : undefined,
    beforeRenderFns: ssrContext ? ssrContext.beforeRenderFns : undefined,
    beforeSerializeFns: ssrContext ? ssrContext.beforeSerializeFns : undefined,
    ssrContext
  })

  function inject(key, value) {
    if (!key) {
      throw new Error('inject(key, value) has no key provided')
    }
    if (value === undefined) {
      throw new Error(`inject('${key}', value) has no value provided`)
    }

    key = '$' + key
    // Add into app
    app[key] = value
    // Add into context
    if (!app.context[key]) {
      app.context[key] = value
    }

    // Add into store
    store[key] = app[key]

    // Check if plugin not already installed
    const installKey = '__app_' + key + '_installed__'
    if (Vue[installKey]) {
      return
    }
    Vue[installKey] = true
    // Call Vue.use() to install the plugin into vm
    Vue.use(() => {
      if (!Object.prototype.hasOwnProperty.call(Vue.prototype, key)) {
        Object.defineProperty(Vue.prototype, key, {
          get () {
            return this.$root.$options[key]
          }
        })
      }
    })
  }

  // Inject runtime config as $config
  inject('config', config)

  if (process.client) {
    // Replace store state before plugins execution
    if (window.__APP__ && window.__APP__.state) {
      store.replaceState(window.__APP__.state)
    }
  }

  // Add enablePreview(previewData = {}) in context for plugins
  if (process.static && process.client) {
    app.context.enablePreview = function (previewData = {}) {
      app.previewData = Object.assign({}, previewData)
      inject('preview', previewData)
    }
  }
  // Plugin execution

  if (process.server && typeof nuxt_plugin_sentryserver_8fb869ac === 'function') {
    await nuxt_plugin_sentryserver_8fb869ac(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_sentryclient_526a59a2 === 'function') {
    await nuxt_plugin_sentryclient_526a59a2(app.context, inject)
  }

  if (typeof nuxt_plugin_axios_fc625d34 === 'function') {
    await nuxt_plugin_axios_fc625d34(app.context, inject)
  }

  if (typeof nuxt_plugin_axios_3566aa80 === 'function') {
    await nuxt_plugin_axios_3566aa80(app.context, inject)
  }

  if (typeof nuxt_plugin_seo_5e466527 === 'function') {
    await nuxt_plugin_seo_5e466527(app.context, inject)
  }

  if (typeof nuxt_plugin_filters_98405076 === 'function') {
    await nuxt_plugin_filters_98405076(app.context, inject)
  }

  if (typeof nuxt_plugin_tracking_3ed1642d === 'function') {
    await nuxt_plugin_tracking_3ed1642d(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_modals_4f7cf348 === 'function') {
    await nuxt_plugin_modals_4f7cf348(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_gtag_6a810249 === 'function') {
    await nuxt_plugin_gtag_6a810249(app.context, inject)
  }

  if (typeof nuxt_plugin_global_6441a2ce === 'function') {
    await nuxt_plugin_global_6441a2ce(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_directives_d0867c0c === 'function') {
    await nuxt_plugin_directives_d0867c0c(app.context, inject)
  }

  if (typeof nuxt_plugin_copy_6a7f1feb === 'function') {
    await nuxt_plugin_copy_6a7f1feb(app.context, inject)
  }

  if (typeof nuxt_plugin_jsonld_cea01772 === 'function') {
    await nuxt_plugin_jsonld_cea01772(app.context, inject)
  }

  if (process.server && typeof nuxt_plugin_campaign_2a7f72f4 === 'function') {
    await nuxt_plugin_campaign_2a7f72f4(app.context, inject)
  }

  // Lock enablePreview in context
  if (process.static && process.client) {
    app.context.enablePreview = function () {
      console.warn('You cannot call enablePreview() outside a plugin.')
    }
  }

  // Wait for async component to be resolved first
  await new Promise((resolve, reject) => {
    // Ignore 404s rather than blindly replacing URL in browser
    if (process.client) {
      const { route } = router.resolve(app.context.route.fullPath)
      if (!route.matched.length) {
        return resolve()
      }
    }
    router.replace(app.context.route.fullPath, resolve, (err) => {
      // https://github.com/vuejs/vue-router/blob/v3.4.3/src/util/errors.js
      if (!err._isRouter) return reject(err)
      if (err.type !== 2 /* NavigationFailureType.redirected */) return resolve()

      // navigated to a different route in router guard
      const unregister = router.afterEach(async (to, from) => {
        if (process.server && ssrContext && ssrContext.url) {
          ssrContext.url = to.fullPath
        }
        app.context.route = await getRouteData(to)
        app.context.params = to.params || {}
        app.context.query = to.query || {}
        unregister()
        resolve()
      })
    })
  })

  return {
    store,
    app,
    router
  }
}

export { createApp, NuxtError }
